import React from 'react';
import './CMSContentElement.css';

const CMSContentElement = ({ 
  contentTypeId,
  contentType,
  content,
  displayType = 'list',
  limit = 10,
  sortBy = 'createdAt',
  sortOrder = 'desc',
  children 
}) => {
  // Remove loading state and fetch logic since data is passed as props
  if (!content || !content.length) {
    return (
      <div className="cms-content-empty">
        {contentTypeId ? 'No content available for this type' : 'Please select a content type'}
      </div>
    );
  }

  // Function to replace content variables in text
  const replaceContentVariables = (text, item) => {
    if (!text) return text;
    
    return text.replace(/\{\{(.*?)\}\}/g, (match, variable) => {
      const field = variable.trim();
      return item.data[field] || match;
    });
  };

  // Function to clone and modify element with content data
  const cloneElementWithContent = (element, item) => {
    // console.log("Clone Element", element, item);
    if (!element) return null;

    // If element is a text node, replace variables
    if (typeof element === 'string') {
      return replaceContentVariables(element, item);
    }

    // Handle special element types
    const elementType = element.type;
    let newProps = {
      ...element.props,
      key: `${element.key || element.props.id}-${item._id}`,
    };

    // Handle specific element types
    switch (elementType) {
      case 'img':
        if (element.props.src) {
          newProps.src = replaceContentVariables(element.props.src, item);
        }
        if (element.props.alt) {
          newProps.alt = replaceContentVariables(element.props.alt, item);
        }
        break;

      case 'a':
        if (element.props.href) {
          newProps.href = replaceContentVariables(element.props.href, item);
        }
        // Add children handling for links
        if (element.props.children) {
          newProps.children = React.Children.map(element.props.children, child =>
            cloneElementWithContent(child, item)
          );
        }
        break;

      case 'div':
      case 'span':
      case 'p':
        // Add data attributes for content type and item id
        newProps['data-content-type'] = contentTypeId;
        newProps['data-content-id'] = item._id;
        // Add children handling for these elements
        if (element.props.children) {
          newProps.children = React.Children.map(element.props.children, child =>
            cloneElementWithContent(child, item)
          );
        }
        break;

      default:
        // For elements with text content
        if (element.props.children) {
          newProps.children = React.Children.map(element.props.children, child =>
            cloneElementWithContent(child, item)
          );
        }
    }

    // Replace any text content in the element
    if (element.props.text) {
      newProps.text = replaceContentVariables(element.props.text, item);
    }

    // Add hover effect for editable content
    if (element.props.className) {
      newProps.className = `${element.props.className} cms-content-editable`;
    } else {
      newProps.className = 'cms-content-editable';
    }

    return React.cloneElement(element, newProps);
  };

  // Sort and limit content items
  const sortedContent = [...content]
    .sort((a, b) => {
      const aValue = a[sortBy] || a.createdAt;
      const bValue = b[sortBy] || b.createdAt;
      return sortOrder === 'desc' ? 
        new Date(bValue) - new Date(aValue) : 
        new Date(aValue) - new Date(bValue);
    })
    .slice(0, limit);

  return (
    <div className={`cms-content-container ${displayType}`}>
      {sortedContent.map(item => (
        <div 
          key={item._id} 
          className={`cms-content-item ${displayType}`}
          data-content-type={contentTypeId}
          data-content-id={item._id}
        >
          {typeof children === 'function' 
            ? children(item)  // Call children as a function with the content item
            : React.Children.map(children, child =>
                React.cloneElement(child, { selectedContentItem: item })
              )
          }
        </div>
      ))}
    </div>
  );
};

export default CMSContentElement; 